import React, { useState, useEffect } from 'react';
// Removed Card imports - using design system classes instead
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Heart, MessageCircle, Eye, Lock, Unlock, Calendar, X, Crown } from "lucide-react";
import { creatorSpaceApi, type TimelinePost } from '@/lib/api/creatorSpace';
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { extractTextFromMaybeJson } from '@/lib/utils/text';
import { getImageUrl, getCreatorAvatarUrl } from '@/lib/utils/image';
import { BlurredMedia } from '@/components/BlurredMedia';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import type { CreatorProfile } from '@/lib/api/creatorSpace';
import { isVideoUrl } from '@/lib/media';
import { VStack, HStack, Grid } from '@/components/core';

interface CreatorTimelineProps {
  className?: string;
  creatorId?: number; // If provided, only show posts from this creator
  maxWidth?: string; // Custom max width override
  title?: string; // Custom title override
}

export const CreatorTimeline: React.FC<CreatorTimelineProps> = ({ 
  className, 
  creatorId, 
  maxWidth = "lg:max-w-[40%]", 
  title = "Creator Timeline" 
}) => {
  const [posts, setPosts] = useState<TimelinePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();
  const ageVerified = user?.is_adult_verified || false;
  const [isPreview, setIsPreview] = useState(false);
  const { t } = useTranslation(['creatorSpace', 'common']);
  const [creator, setCreator] = useState<CreatorProfile | null>(null);
  const creatorCacheRef = React.useRef<Map<number, CreatorProfile>>(new Map());
  const navigate = useNavigate();

  const buildPreviewPosts = (): TimelinePost[] => {
    const now = new Date().toISOString();
    const base: TimelinePost = {
      id: -1,
      creator_id: creatorId || 0,
      title: t('timeline.previewPost'),
      content: t('timeline.previewContent'),
      post_type: 'image',
      media_urls: ['/placeholder.svg'],
      thumbnail_url: '/placeholder.svg',
      is_paid: true,
      price: 10,
      currency: 'Coins',
      likes_count: 0,
      comments_count: 0,
      views_count: 0,
      status: 'published',
      published_at: now,
      generated_by_ai: false,
      created_at: now,
      updated_at: now,
      user_has_liked: false,
      user_has_unlocked: false,
      creator_name: creator?.character?.name || 'Creator',
      is_subscribed: false,
    };
    return [
      base,
      { ...base, id: -2, title: t('timeline.previewGallery'), post_type: 'image' },
    ];
  };

  const loadPosts = async (pageNum = 0, append = false) => {
    try {
      if (pageNum === 0) setLoading(true);
      else setLoadingMore(true);

      // Use creator-specific posts if creatorId is provided, otherwise use general timeline
      if (creatorId) {
        const rawPosts = await creatorSpaceApi.getCreatorPosts(creatorId, pageNum * 20, 20);
        const mappedPosts: TimelinePost[] = (rawPosts as any[]).map((p: any) => ({
          ...p,
          // Bei creator-spezifischen Posts liefert das Backend evtl. keinen Namen/Avatar – kein Hardcoding
          creator_name: (p?.creator_name ?? '').trim(),
          creator_avatar: (p?.creator_avatar ?? '').trim(),
          is_subscribed: true,
        }) as TimelinePost);
        const enriched = await enrichPostsWithCreatorInfo(mappedPosts);
        if (append) {
          setPosts(prev => [...prev, ...enriched]);
        } else {
          setPosts(enriched);
        }
        setHasMore(mappedPosts.length === 20);
        setPage(pageNum);
      } else {
        const newPosts = await creatorSpaceApi.getTimeline(pageNum * 20, 20);
        const enriched = await enrichPostsWithCreatorInfo(newPosts);
        if (append) {
          setPosts(prev => [...prev, ...enriched]);
        } else {
          setPosts(enriched);
        }
        setHasMore(newPosts.length === 20);
        setPage(pageNum);
      }
    } catch (error: any) {
      // Preview-Modus: bei 403 (z. B. nicht abonniert) oder 5xx (Serverfehler) fallback anzeigen
      const status = error?.response?.status;
      if (creatorId && (status === 403 || (status && status >= 500))) {
        setIsPreview(true);
        const preview = buildPreviewPosts();
        if (append) {
          setPosts(prev => [...prev, ...preview]);
        } else {
          setPosts(preview);
        }
        setHasMore(false);
      } else {
        console.error('Error loading timeline:', error);
        toast({
          title: t('common:errors.error'),
          description: t('timeline.loadError'),
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    void loadPosts();
  }, [creatorId]);

  // Lade Creator-Profil für Fallback von Name/Avatar, wenn eine spezifische Creator-Timeline angezeigt wird
  useEffect(() => {
    let cancelled = false;
    const fetchCreator = async () => {
      if (!creatorId) {
        setCreator(null);
        return;
      }
      try {
        const data = await creatorSpaceApi.getCreator(creatorId);
        if (!cancelled) setCreator(data);
        // cache
        if (!cancelled && data?.id) {
          creatorCacheRef.current.set(data.id, data);
        }
      } catch (_) {
        if (!cancelled) setCreator(null);
      }
    };
    void fetchCreator();
    return () => { cancelled = true; };
  }, [creatorId]);

  // Hilfsfunktion: Ergänzt fehlende Creator-Namen/Avatare durch Profilabfragen (mit Cache)
  const enrichPostsWithCreatorInfo = async (inputPosts: TimelinePost[]): Promise<TimelinePost[]> => {
    try {
      const missingIds = Array.from(
        new Set(
          inputPosts
            .filter(p => {
              const nameMissing = !p.creator_name?.trim();
              const avatarMissing = !p.creator_avatar || !String(p.creator_avatar).trim();
              const avatarLooksVideo = !avatarMissing && isVideoUrl(String(p.creator_avatar));
              return nameMissing || avatarMissing || avatarLooksVideo;
            })
            .map(p => p.creator_id)
        )
      ).filter(Boolean);

      const toFetch = missingIds.filter(id => !creatorCacheRef.current.has(id));

      if (toFetch.length > 0) {
        const fetched = await Promise.allSettled(toFetch.map(id => creatorSpaceApi.getCreator(id)));
        fetched.forEach((res, idx) => {
          const id = toFetch[idx]!;
          if (res.status === 'fulfilled' && res.value) {
            creatorCacheRef.current.set(id, res.value);
          }
        });
      }

      return inputPosts.map(p => {
        const cached = creatorCacheRef.current.get(p.creator_id);
        if (!cached) return p;
        const name = (p.creator_name && p.creator_name.trim()) || (cached.character?.name || '');
        const cachedAvatar = (getCreatorAvatarUrl(cached, ageVerified) || '').trim();
        const incomingAvatar = (p.creator_avatar?.trim()) || '';
        // Bevorzuge das berechnete Avatar (liefert Thumbnail bei Video-Profilen),
        // fallback auf das vom Backend mitgelieferte Avatar.
        const avatar = cachedAvatar || incomingAvatar;
        return { ...p, creator_name: name, creator_avatar: avatar } as TimelinePost;
      });
    } catch (_) {
      return inputPosts;
    }
  };

  const handleLike = async (postId: number) => {
    try {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      if (post.user_has_liked) {
        await creatorSpaceApi.unlikePost(postId);
      } else {
        await creatorSpaceApi.likePost(postId);
      }

      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        const wasLiked = !!p.user_has_liked;
        const delta = wasLiked ? -1 : 1;
        const currentLikes = p.likes_count ?? 0;
        const currentDisplay = (p as any).display_likes_count as number | undefined;
        const nextDisplay = typeof currentDisplay === 'number' ? currentDisplay + delta : currentDisplay;
        return {
          ...p,
          user_has_liked: !wasLiked,
          likes_count: currentLikes + delta,
          display_likes_count: nextDisplay,
        } as any;
      }));
    } catch (error: any) {
      const status = error?.response?.status;
      // Bei fehlender Berechtigung (nicht abonniert/unauthenticated) kein lauter Toast
      if (status === 401 || status === 403) {
        return;
      }
      console.error('Error liking post:', error);
      toast({
        title: t('common:notifications.error'),
        description: t('errors.likeFailed'),
        variant: "destructive"
      });
    }
  };

  const handleUnlock = async (postId: number) => {
    try {
      await creatorSpaceApi.unlockPost(postId);
      setPosts(prev => prev.map(p => 
        p.id === postId 
          ? { ...p, user_has_unlocked: true }
          : p
      ));
      toast({
        title: t('common:notifications.success'),
        description: t('unlock.success'),
      });
    } catch (error: any) {
      const status = error?.response?.status;
      // Nicht abonniert / nicht eingeloggt: UI zeigt bereits Overlay/Vorschau → kein Fehler-Toast
      if (status === 401 || status === 403) {
        return;
      }
      console.error('Error unlocking post:', error);
      toast({
        title: t('common:notifications.error'),
        description: t('errors.unlockFailed'),
        variant: "destructive"
      });
    }
  };

  // Track real views when a post becomes visible in the viewport
  React.useEffect(() => {
    if (!posts || posts.length === 0) return;
    const observed = new Set<number>();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idStr = entry.target.getAttribute('data-post-id');
          const id = idStr ? parseInt(idStr, 10) : NaN;
          if (!Number.isNaN(id) && !observed.has(id)) {
            observed.add(id);
            // Fire and forget, Fehler still abfangen (z. B. 400 Cannot record view)
            void creatorSpaceApi.viewPost(id).catch(() => {/* silent */});
          }
        }
      });
    }, { threshold: 0.35 });

    const nodes = document.querySelectorAll('[data-post-id]');
    nodes.forEach(n => observer.observe(n));

    return () => {
      observer.disconnect();
    };
  }, [posts]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      void loadPosts(page + 1, true);
    }
  };

  if (loading) {
    return (
      <HStack justify="center" align="center" className="h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </HStack>
    );
  }

  return (
    <VStack gap="lg" className={cn(`w-full ${maxWidth} mx-auto`, className)}>
      <HStack justify="between" align="center" className="mb-token-lg">
        <h3 className="text-xl font-bold design-text-primary">{title}</h3>
        <Button
          variant="glass"
          onClick={() => { void loadPosts(0, false); }}
          disabled={loading}
        >
          {t('timeline.update')}
        </Button>
      </HStack>

      {isPreview && creatorId && (
        <div className="design-card p-token-lg border border-warning/30 bg-warning/5">
          <HStack gap="md" align="start">
            <Lock className="h-6 w-6 text-warning mt-token-xs" />
            <div>
              <h4 className="text-lg font-semibold design-text-primary mb-token-xs">{t('timeline.previewMode')}</h4>
              <p className="design-text-secondary mb-token-sm">
                {t('timeline.previewDesc')}
              </p>
              <HStack gap="sm">
                <Button
                  size="sm"
                  variant="gradient"
                  onClick={() => {
                    const subscribeButton = document.querySelector<HTMLButtonElement>('[data-subscribe-button]');
                    if (subscribeButton) {
                      subscribeButton.click();
                    } else {
                      navigate(`/creator/${creatorId}`);
                    }
                  }}
                >
                  {t('profile.subscribe', { price: Math.round(creator?.monthly_price ?? 0) })}
                </Button>
              </HStack>
            </div>
          </HStack>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="design-card p-12 text-center">
          <Calendar className="h-16 w-16 mx-auto mb-token-md design-text-secondary" />
          <h4 className="text-lg font-semibold design-text-primary mb-token-sm">{t('timeline.noPosts')}</h4>
          <p className="design-text-secondary">
            {t('timeline.noPostsDesc')}
          </p>
        </div>
      ) : (
        <VStack gap="lg">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onLike={handleLike}
              onUnlock={handleUnlock}
              ageVerified={ageVerified}
              creatorFallbackName={creator?.character?.name || ''}
              creatorFallbackAvatar={creator ? getCreatorAvatarUrl(creator, ageVerified) : ''}
            />
          ))}
          
          {hasMore && (
            <HStack justify="center">
              <Button
                variant="glass"
                onClick={() => { loadMore(); }}
                disabled={loadingMore}
              >
                {loadingMore ? t('timeline.loading') : t('timeline.loadMore')}
              </Button>
            </HStack>
          )}
        </VStack>
      )}
    </VStack>
  );
};

interface PostCardProps {
  post: TimelinePost;
  onLike: (postId: number) => void;
  onUnlock: (postId: number) => void;
  ageVerified: boolean;
  creatorFallbackName: string;
  creatorFallbackAvatar: string;
}

const PostCard: React.FC<PostCardProps> = ({ post, onLike, onUnlock, ageVerified: _ageVerified, creatorFallbackName, creatorFallbackAvatar }) => {
  const { t } = useTranslation(['creatorSpace', 'common']);
  const isEffectivePaid = (post.price ?? 0) > 0;
  const canViewContent = Boolean(
    post.is_subscribed && (!isEffectivePaid || post.user_has_unlocked)
  );
  const displayName = (post.creator_name && post.creator_name.trim()) || creatorFallbackName || 'Creator';
  const displayAvatarUrl = (post.creator_avatar?.trim()) || creatorFallbackAvatar || '';

  return (
    <div className="design-card overflow-hidden" data-post-id={post.id}>
      <div className="p-token-lg pb-token-sm">
        <HStack justify="between" align="center">
          <Link
            to={`/creator/${post.creator_id}`}
            className="hover:opacity-80 transition-opacity"
          >
          <HStack align="center" gap="sm">
            <Avatar className="h-12 w-12">
              <AvatarImage 
                src={displayAvatarUrl ? getImageUrl(displayAvatarUrl) : ''} 
                className="object-cover object-center-top"
              />
              <AvatarFallback>{displayName?.[0] || 'C'}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold design-text-primary">{displayName}</h3>
              <p className="text-sm design-text-secondary">
                {post.creator_username ? `@${post.creator_username} • ` : ''}
                {formatDistanceToNow(new Date(post.created_at), {
                  addSuffix: true,
                  locale: de
                })}
              </p>
            </div>
          </HStack>
          </Link>
          <HStack gap="sm" align="center">
            {isEffectivePaid && (
              <Badge variant="secondary">
                <Lock className="h-3 w-3 mr-token-xs" />
                {post.price} Coins
              </Badge>
            )}
          </HStack>
        </HStack>
      </div>

      <VStack gap="md" className="relative px-token-lg pb-token-lg">
        {post.title && (
          <h4 className="font-semibold text-lg">{post.title}</h4>
        )}

        {/* Content mit Blur-Effekt für bezahlte/nicht abonnierte Inhalte */}
        <div className={cn(
          "transition-all duration-300",
          !canViewContent && "blur-md select-none"
        )}>
          <p className="whitespace-pre-wrap">
            {canViewContent ? post.content : t('overlay.subscribeToSee')}
          </p>
          
          {/* Medieninhalt: bei gesperrtem Inhalt Platzhalter statt echter URLs */}
          {post.media_urls.length > 0 && (
            <div className="mt-token-md">
              {(canViewContent ? post.media_urls : post.media_urls.map(() => '/placeholder.svg')).map((url, index) => (
                <Dialog key={index}>
                  <DialogTrigger asChild>
                    <div className="relative cursor-pointer hover:opacity-90 transition-opacity mb-token-xs">
                      <BlurredMedia
                        src={getImageUrl(url)}
                        alt={`Post media ${index + 1}`}
                        shouldBlur={false}
                        isFsk18={false}
                        className="w-full h-auto rounded-token-button object-cover"
                        autoPlayVideo
                        showMuteToggle
                        loopVideo={false}
                      />
                    </div>
                  </DialogTrigger>
                  {canViewContent && (
                  <DialogContent className="max-w-4xl w-full h-[90vh] p-0 bg-black/95 border-none">
                    <DialogTitle className="sr-only">Post Bild {index + 1}</DialogTitle>
                    <DialogDescription className="sr-only">
                      Vollbild-Ansicht des {index + 1}. Bildes von {post.creator_name}
                    </DialogDescription>
                    <div className="relative w-full h-full flex items-center justify-center">
                      <BlurredMedia
                        src={getImageUrl(url)}
                        alt={`Post media ${index + 1}`}
                        shouldBlur={false}
                        isFsk18={false}
                        className="max-w-full max-h-full object-contain"
                        autoPlayVideo
                        showMuteToggle
                        loopVideo={false}
                      />
                    </div>
                  </DialogContent>
                  )}
                </Dialog>
              ))}
            </div>
          )}
        </div>

        {/* Access control overlay */}
        {!canViewContent && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center bg-white/90 p-token-lg rounded-token-button">
              {!post.is_subscribed ? (
                <div>
                  <Lock className="h-8 w-8 mx-auto mb-token-sm text-muted-foreground" />
                  <p className="font-semibold">{t('overlay.subscriptionRequired')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('overlay.subscribeCreatorToSee')}
                  </p>
                </div>
              ) : (
                <div>
                  <Lock className="h-8 w-8 mx-auto mb-token-sm text-muted-foreground" />
                  <p className="font-semibold">{t('overlay.premiumContent')}</p>
                  <p className="text-sm text-muted-foreground mb-token-sm">
                    {isEffectivePaid ? t('overlay.unlockForCoins', { price: post.price }) : 'Kostenloser Inhalt'}
                  </p>
                  <Button onClick={() => onUnlock(post.id)} size="sm">
                    <Unlock className="h-4 w-4 mr-token-sm" />
                    {t('actions.unlock')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post actions - Design-aware styling */}
        <div className="design-action-bar">
          <HStack gap="sm" align="center">
            {/* Like Action */}
            <button
              onClick={() => onLike(post.id)}
              className={cn(
                "design-action design-action-like",
                post.user_has_liked && "active"
              )}
              data-liked={post.user_has_liked}
            >
              <Heart className={cn(
                "h-5 w-5 transition-all",
                post.user_has_liked && "fill-current"
              )} />
              <span className="design-action-count">
                {(post as any).display_likes_count ?? post.likes_count}
              </span>
            </button>
            
            {/* Comment Action */}
            <button
              className={cn(
                "design-action design-action-comment",
                post.comments_count > 0 && "active"
              )}
              data-has-comments={post.comments_count > 0}
            >
              <MessageCircle className="h-5 w-5" />
              <span className="design-action-count">
                {post.comments_count}
              </span>
            </button>
            
            {/* View Action */}
            <div className="design-action design-action-view cursor-default">
              <Eye className="h-5 w-5" />
              <span className="design-action-count">
                {(post as any).display_views_count ?? post.views_count}
              </span>
            </div>
          </HStack>
        </div>

        {/* Comments section - always visible */}
        {canViewContent && (
          <div className="mt-token-md pt-token-md border-t border-white/10">
            <PostComments 
              postId={post.id} 
              isSubscribed={post.is_subscribed} 
              creatorId={post.creator_id}
              creatorName={post.creator_name || ''}
              creatorAvatar={post.creator_avatar}
            />
          </div>
        )}
      </VStack>
    </div>
  );
};

interface PostCommentsProps {
  postId: number;
  isSubscribed: boolean;
  creatorId: number;
  creatorName: string;
  creatorAvatar?: string;
}

const PostComments: React.FC<PostCommentsProps> = ({ postId, isSubscribed, creatorId, creatorName, creatorAvatar }) => {
  const [comments, setComments] = useState<any[]>([]);
  const { t } = useTranslation(['creatorSpace', 'common']);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const { toast } = useToast();

  const loadComments = async () => {
    try {
      setLoading(true);
      const commentsData = await creatorSpaceApi.getPostComments(postId);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const parentId = replyingTo ?? undefined;
      const comment = await creatorSpaceApi.createComment(postId, newComment, parentId);

      if (replyingTo) {
        // Replies werden nur auf Ebene 2 angezeigt: neue Antwort immer dem Top-Level-Elternkommentar zuordnen
        setComments(prev => prev.map((topLevel: any) => {
          if (topLevel.id === replyingTo) {
            return { ...topLevel, replies: [...(topLevel.replies || []), comment] };
          }
          if (topLevel.replies?.some((r: any) => r.id === replyingTo)) {
            return { ...topLevel, replies: [...topLevel.replies, comment] };
          }
          return topLevel;
        }));
      } else {
        // Top-level comment
        setComments(prev => [...prev, comment]);
      }
      
      setNewComment('');
      setReplyingTo(null);
      toast({
        title: t('common:notifications.success'),
        description: replyingTo ? t('comments.replyPosted') : t('comments.commentPosted'),
      });
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: t('common:notifications.error'),
        description: t('comments.commentFailed'),
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    void loadComments();
  }, [postId]);

  // Only show first 5 comments unless "show all" is clicked
  const displayedComments = showAllComments ? comments : comments.slice(0, 5);

  const getUserDisplayName = (c: any): string => {
    // Prüfe zuerst auf simulierte Kommentare
    if (c?.is_simulated) {
      const simDisplay = ((c?.simulated_display_name ?? '') as string).trim();
      const simUsername = ((c?.simulated_username ?? '') as string).trim();
      if (simDisplay || simUsername) {
        return simDisplay || simUsername;
      }
    }
    // Fallback für echte User
    const display = ((c?.user?.display_name ?? (c?.user)?.display_name ?? '') as string).trim();
    const username = ((c?.user?.username ?? '') as string).trim();
    const user_name = ((c?.user_name ?? '') as string).trim();
    return display || username || user_name || t('comments.anonymousUser');
  };

  // Generiere Avatar-URL für simulierte Kommentare (DiceBear)
  const getSimulatedAvatarUrl = (seed: string | undefined): string => {
    const safeSeed = seed || 'default';
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(safeSeed)}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
  };

  // Get the name we're replying to
  const getReplyingToName = (): string | null => {
    if (!replyingTo) return null;
    for (const comment of comments) {
      if (comment.id === replyingTo) {
        return getUserDisplayName(comment);
      }
      if (comment.replies) {
        for (const reply of comment.replies) {
          if (reply.id === replyingTo) {
            return getUserDisplayName(reply);
          }
        }
      }
    }
    return null;
  };

  const replyingToName = getReplyingToName();

  return (
    <VStack gap="md">
      {/* Comment Input Section - Vibrant & Inviting Design */}
      {isSubscribed && (
        <div className="relative">
          {/* Reply indicator */}
          {replyingTo && replyingToName && (
            <HStack gap="sm" align="center" className="mb-token-xs px-token-md py-token-xs bg-brand-primary/30 rounded-t-token-card border-2 border-b-0 border-brand-primary/50">
              <span className="text-sm text-brand-primary-light font-medium">
                {t('comments.replyingTo', { name: replyingToName })}
              </span>
              <button
                onClick={() => setReplyingTo(null)}
                className="ml-auto text-white/80 hover:text-white transition-colors p-token-xs hover:bg-white/10 rounded"
                aria-label={t('common:buttons.cancel')}
              >
                <X className="h-4 w-4" />
              </button>
            </HStack>
          )}
          
          <HStack gap="md" className={cn(
            "p-token-md",
            "bg-white/[0.08] border-2 border-white/20",
            replyingTo ? "rounded-b-token-card" : "rounded-token-card",
            "hover:bg-white/[0.12] hover:border-white/30",
            "design-input-focus-glow",
            "transition-all duration-200"
          )}>
            <Input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={replyingTo ? t('input.replyPlaceholder') : t('input.commentPlaceholder')}
              className="flex-1 bg-white/10 border-2 border-white/20 rounded-token-button h-12 px-token-md text-white placeholder:text-white/50 focus-visible:ring-0 design-input-focus-border transition-all"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submitComment(); } }}
            />
            <Button
              onClick={submitComment}
              disabled={submitting || !newComment.trim()}
              className="bg-gradient-to-r from-brand-primary to-brand-secondary hover:from-brand-primary hover:to-brand-secondary disabled:from-muted disabled:to-muted-foreground disabled:opacity-50 text-white border-0 shadow-token-modal shadow-brand-primary/30 hover:shadow-brand-primary/50 px-token-lg font-bold h-12 text-base transition-all duration-200"
            >
              {submitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                t('common:buttons.send')
              )}
            </Button>
          </HStack>
        </div>
      )}

      {loading ? (
        <VStack align="center" className="py-token-xl">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-brand-primary border-t-transparent"></div>
          <p className="text-sm text-white/70 mt-token-sm">{t('comments.loading')}</p>
        </VStack>
      ) : comments.length === 0 ? (
        <VStack align="center" className="py-token-xl px-token-md bg-white/[0.03] rounded-token-card border border-white/10">
          <HStack align="center" justify="center" className="w-16 h-16 mb-token-md rounded-full bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20">
            <MessageCircle className="h-8 w-8 text-brand-primary-light/80" />
          </HStack>
          <p className="text-base text-white/80 font-medium">
            {t('comments.noComments')}
          </p>
          {isSubscribed && (
            <p className="text-sm text-brand-primary-light/80 mt-token-xs">
              {t('comments.beFirst')}
            </p>
          )}
        </VStack>
      ) : (
        <>
          <VStack gap="md">
            {displayedComments.map((comment) => (
              <VStack key={comment.id} gap="sm">
                {/* User Comment */}
                <div className="bg-muted/30 p-token-md rounded-token-card border border-white/5">
                  <HStack gap="md" align="start">
                    {/* Avatar - DiceBear für simulierte Kommentare, User-Avatar wenn vorhanden, sonst Buchstabe */}
                    {comment.is_simulated && comment.simulated_avatar_seed ? (
                      <img
                        src={getSimulatedAvatarUrl(comment.simulated_avatar_seed)}
                        alt={getUserDisplayName(comment)}
                        className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                      />
                    ) : comment.user?.avatar_url ? (
                      <img
                        src={comment.user.avatar_url}
                        alt={getUserDisplayName(comment)}
                        className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                      />
                    ) : (
                      <HStack align="center" justify="center" className="w-8 h-8 rounded-full bg-gradient-to-br from-muted to-muted-foreground text-white text-xs font-medium flex-shrink-0">
                        {getUserDisplayName(comment).charAt(0).toUpperCase()}
                      </HStack>
                    )}
                    <div className="flex-1 min-w-0">
                      <HStack gap="sm" align="center" wrap>
                        <p className="text-sm font-semibold design-text-primary">
                          {getUserDisplayName(comment)}
                        </p>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(comment.created_at), {
                            addSuffix: true,
                            locale: de
                          })}
                        </span>
                      </HStack>
                      <p className="text-sm mt-1.5 design-text-secondary leading-relaxed">{comment.content}</p>
                      {isSubscribed && !comment.ai_response && (
                        <button
                          onClick={() => setReplyingTo(comment.id)}
                          className="text-xs text-muted-foreground hover:text-primary mt-token-xs transition-colors"
                        >
                          {t('buttons.reply')}
                        </button>
                      )}
                    </div>
                  </HStack>
                </div>
                
                {/* Creator/AI Response - Highlighted Design */}
                {comment.ai_response && (
                  <div className="ml-token-lg relative">
                    {/* Vertical connection line */}
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-primary to-brand-secondary rounded-full" />
                    
                    <div className="ml-token-md p-token-md rounded-token-card bg-gradient-to-br from-brand-primary/10 via-brand-secondary/10 to-brand-primary/5 border border-brand-primary/20 shadow-token-card shadow-brand-primary/5">
                      <HStack gap="md" align="start">
                        {/* Creator Avatar with Crown */}
                        <div className="relative flex-shrink-0">
                          <HStack align="center" justify="center" className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-brand-primary to-brand-secondary text-white text-xs font-medium">
                            {creatorAvatar ? (
                              <img
                                src={creatorAvatar}
                                alt={((comment.creator_name?.trim()) || creatorName || 'Creator')}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // On error, hide image and show fallback letter
                                  const img = e.target as HTMLImageElement;
                                  img.style.display = 'none';
                                  // Show the sibling span (fallback letter)
                                  const sibling = img.nextElementSibling as HTMLElement | null;
                                  if (sibling) sibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            {/* Fallback letter - shown if no avatar or on image error */}
                            <span style={{ display: creatorAvatar ? 'none' : 'block' }}>
                              {((comment.creator_name?.trim()) || creatorName || 'C').charAt(0).toUpperCase()}
                            </span>
                          </HStack>
                          <div className="absolute -top-1 -right-1 bg-warning rounded-full p-0.5">
                            <Crown className="h-2.5 w-2.5 text-warning-foreground" />
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <HStack gap="sm" align="center" wrap>
                            <Link
                              to={`/creator/${creatorId}`}
                              className="text-sm font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent hover:from-brand-primary-light hover:to-brand-secondary-light transition-all"
                            >
                              {(comment.creator_name?.trim()) || creatorName || 'Creator'}
                            </Link>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-gradient-to-r from-brand-primary/20 to-brand-secondary/20 border-brand-primary/30 text-brand-primary-light">
                              Creator
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {comment.ai_responded_at && formatDistanceToNow(new Date(comment.ai_responded_at), {
                                addSuffix: true,
                                locale: de
                              })}
                            </span>
                          </HStack>
                          <p className="text-sm mt-1.5 design-text-primary leading-relaxed">{extractTextFromMaybeJson(comment.ai_response)}</p>
                          {isSubscribed && (
                            <button
                              onClick={() => setReplyingTo(comment.id)}
                              className="text-xs text-brand-primary-light hover:text-brand-primary-light mt-token-xs transition-colors"
                            >
                              {t('buttons.reply')}
                            </button>
                          )}
                        </div>
                      </HStack>
                    </div>
                  </div>
                )}

                {/* Replies: Level 2 only */}
                {comment.replies && comment.replies.length > 0 && (
                  <VStack className="ml-token-lg" gap="sm">
                    {comment.replies.map((reply: any) => {
                      const isCreatorReply = reply.is_from_creator || reply.ai_response;
                      
                      return (
                        <div key={reply.id} className="relative">
                          {/* Vertical connection line */}
                          <div className={cn(
                            "absolute left-0 top-0 bottom-0 w-0.5 rounded-full",
                            isCreatorReply 
                              ? "bg-gradient-to-b from-brand-primary to-brand-secondary" 
                              : "bg-muted/50"
                          )} />
                          
                          <div className={cn(
                            "ml-token-md p-token-md rounded-token-card",
                            isCreatorReply
                              ? "bg-gradient-to-br from-brand-primary/10 via-brand-secondary/10 to-brand-primary/5 border border-brand-primary/20"
                              : "bg-muted/20 border border-white/5"
                          )}>
                            <HStack align="start" gap="sm">
                              {/* Avatar */}
                              <div className="relative flex-shrink-0">
                                <HStack
                                  align="center"
                                  justify="center"
                                  className={cn(
                                    "w-7 h-7 rounded-full text-white text-xs font-medium",
                                    isCreatorReply
                                      ? "bg-gradient-to-br from-brand-primary to-brand-secondary"
                                      : "bg-gradient-to-br from-muted to-muted-foreground"
                                  )}
                                >
                                  {getUserDisplayName(reply).charAt(0).toUpperCase()}
                                </HStack>
                                {isCreatorReply && (
                                  <div className="absolute -top-1 -right-1 bg-warning rounded-full p-0.5">
                                    <Crown className="h-2 w-2 text-warning-foreground" />
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <HStack align="center" wrap gap="sm">
                                  {isCreatorReply ? (
                                    <>
                                      <Link
                                        to={`/creator/${creatorId}`}
                                        className="text-sm font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent hover:from-brand-primary-light hover:to-brand-secondary-light"
                                      >
                                        {(reply.creator_name?.trim()) || creatorName || 'Creator'}
                                      </Link>
                                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-gradient-to-r from-brand-primary/20 to-brand-secondary/20 border-brand-primary/30 text-brand-primary-light">
                                        Creator
                                      </Badge>
                                    </>
                                  ) : (
                                    <p className="text-sm font-semibold design-text-primary">
                                      {getUserDisplayName(reply)}
                                    </p>
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(reply.created_at), { 
                                      addSuffix: true,
                                      locale: de
                                    })}
                                  </span>
                                </HStack>
                                <p className={cn(
                                  "text-sm mt-1.5 leading-relaxed",
                                  isCreatorReply ? "design-text-primary" : "design-text-secondary"
                                )}>
                                  {reply.content}
                                </p>
                                {isSubscribed && (
                                  <button
                                    onClick={() => setReplyingTo(reply.id)}
                                    className={cn(
                                      "text-xs mt-token-xs transition-colors",
                                      isCreatorReply 
                                        ? "text-brand-primary-light hover:text-brand-primary-light" 
                                        : "text-muted-foreground hover:text-primary"
                                    )}
                                  >
                                    {t('buttons.reply')}
                                  </button>
                                )}
                              </div>
                            </HStack>
                            
                            {/* AI response to reply */}
                            {reply.ai_response && !isCreatorReply && (
                              <div className="mt-token-sm pt-token-sm border-t border-brand-primary/20">
                                <HStack align="start" gap="sm">
                                  <div className="relative flex-shrink-0">
                                    <HStack align="center" justify="center" className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary text-white text-xs font-medium">
                                      {((reply.creator_name?.trim()) || creatorName || 'C').charAt(0).toUpperCase()}
                                    </HStack>
                                    <div className="absolute -top-1 -right-1 bg-warning rounded-full p-0.5">
                                      <Crown className="h-2 w-2 text-warning-foreground" />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <HStack align="center" wrap gap="sm">
                                      <Link
                                        to={`/creator/${creatorId}`}
                                        className="text-sm font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent"
                                      >
                                        {(reply.creator_name?.trim()) || creatorName || 'Creator'}
                                      </Link>
                                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-gradient-to-r from-brand-primary/20 to-brand-secondary/20 border-brand-primary/30 text-brand-primary-light">
                                        Creator
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        {reply.ai_responded_at && formatDistanceToNow(new Date(reply.ai_responded_at), {
                                          addSuffix: true,
                                          locale: de
                                        })}
                                      </span>
                                    </HStack>
                                    <p className="text-sm mt-1.5 design-text-primary leading-relaxed">{extractTextFromMaybeJson(reply.ai_response)}</p>
                                  </div>
                                </HStack>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </VStack>
                )}
              </VStack>
            ))}
          </VStack>

          {/* Show all comments button */}
          {comments.length > 5 && !showAllComments && (
            <HStack justify="center" className="pt-token-xs">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllComments(true)}
                className="text-brand-primary-light hover:text-brand-primary-light hover:bg-brand-primary/10"
              >
                {t('comments.showAll', { count: comments.length })}
              </Button>
            </HStack>
          )}

          {/* Show less button */}
          {showAllComments && comments.length > 5 && (
            <HStack justify="center" className="pt-token-xs">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllComments(false)}
                className="text-muted-foreground hover:text-primary"
              >
                {t('comments.showLess')}
              </Button>
            </HStack>
          )}
        </>
      )}
    </VStack>
  );
};